<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconCartPlus from "@js/components/Icons/IconCartPlus.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed} from "vue";
import CartUnitSwitch from "@js/components/Sections/Cart/Component/CartUnitSwitch.vue";
import CartQuantityStepper from "@js/components/Sections/Cart/Component/CartQuantityStepper.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import IconTrash from "@js/components/Icons/IconTrash.vue";

export default {
    name: "CatalogListItem",
    components: {
        IconTrash,
        CartQuantityStepper,
        CartUnitSwitch,
        OnlyMobile,
        OnlyDesktop,
        IconCheckMark,
        IconScales,
        NumberFormatComponent,
        IconCartPlus,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link
    },
    props: {
        data: { type: Object, required: true },
        defaultMode: { type: String, default: "m" }, // "m" | "t"
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cart = useCartStore();
        const catalogStore = useCatalogStore();

        // если есть цена за штуку — работаем в режиме pc и не показываем переключатель
        const initialMode = props.data.price_per_piece ? 'pc' : props.defaultMode;
        cart.registerCatalogProduct(props.data, initialMode);

        const modeProxy = computed({
            get: () => cart.uiOf(props.data.id).mode,
            set: (val) => cart.setMode(props.data.id, val),
        });

        const qtyProxy = computed({
            get: () => cart.uiOf(props.data.id).qty,
            set: (val) => cart.setQtyFromInput(props.data.id, val),
        });

        const showUnitSwitch = computed(() => !props.data.price_per_piece);

        const unitText = computed(() => {
            const u = cart.unitLabel(props.data.id); // 'м' | 'т' | 'шт'
            if (u === 'м') return '/метр';
            if (u === 'т') return '/тонна';
            return '/шт';
        });

        const hasAnyPrice = computed(() =>
            !!(props.data.price_per_meter || props.data.price_per_ton || props.data.price_per_piece)
        );

        return { catalogStore, likeStore, compareStore, cart, modeProxy, qtyProxy, showUnitSwitch, unitText, hasAnyPrice };
    },
};
</script>

<template>
    <div class="relative w-full bg-white p-3 md:p-4">
        <div class="flex h-full gap-4 flex-col md:flex-row md:items-center md:gap-6">
            <!-- Картинка + бейджи -->
            <Link :href="route('product', data.slug)" class="relative flex shrink-0 items-center gap-2 w-full md:w-40">
                <div class="flex items-center justify-center overflow-hidden rounded-xl border border-gray-200 w-full">
                    <OnlyDesktop :maxSize="1024">
                        <ImageLoader :title="data.title"
                                     :src="'/storage/assets/products/'+data.category_slug+'/preview/'+ data.images.preview_square"
                                     imgClasses="w-full h-full object-contain"/>
                    </OnlyDesktop>
                    <OnlyMobile :maxSize="1024">
                        <ImageLoader :title="data.title"
                                     :src="'/storage/assets/products/'+data.category_slug+'/preview/'+ data.images.preview_wide"
                                     imgClasses="w-full h-full object-contain"/>
                    </OnlyMobile>
                </div>
                <div class="absolute left-0 top-0 flex gap-1">
                    <span v-if="data.is_promo" class="rounded bg-amber-400 px-2 py-0.5 text-[11px] font-medium text-black">Акция</span>
                    <span v-if="data.is_hit" class="rounded bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">Хит</span>
                </div>
            </Link>

            <!-- Центр -->
            <div class="flex min-w-0 grow flex-col gap-2">
                <div class="w-min text-nowrap text-[11px] px-2 py-0.5 rounded"
                     :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                    {{ data.available ? 'В наличии' : 'Ожидаем поставку' }}
                </div>

                <Link :href="route('product', data.slug)"
                      class="max-w-[400px] line-clamp-2 text-base font-medium leading-5 text-gray-900 hover:text-navHoverColor transition">
                    {{ data.title }}
                </Link>

                <!-- Параметры -->
                <div class="mb-3 flex flex-wrap gap-2 text-xs font-medium text-gray-700">
          <span v-if="cart.lengthM(data.id)" class="rounded bg-grayBg px-2 py-1">
            Длина:  <NumberFormatComponent :number="cart.lengthM(data.id)" append_text=" м"/>
          </span>
                    <span v-if="cart.weightKg(data.id)" class="rounded bg-grayBg px-2 py-1">
            Вес: <NumberFormatComponent :number="cart.weightKg(data.id)" append_text=" кг"/>
          </span>
                </div>

                <!-- Цена и единицы -->
                <div v-if="hasAnyPrice" class="flex flex-wrap items-center gap-3">
                    <CartUnitSwitch
                        v-if="showUnitSwitch"
                        v-model="modeProxy"
                        :disabled-m="!data.price_per_meter"
                        :disabled-t="!data.price_per_ton"
                    />
                    <div v-else class="font-medium">Цена за штуку:</div>

                    <div>
                        <div class="text-sm text-gray-700">
                            <NumberFormatComponent
                                :number="cart.priceActive(data.id)"
                                append_text=" ₽"
                                class="text-lg font-semibold text-black"/>
                            <span class="font-medium">{{ unitText }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Правая колонка -->
            <div class="flex flex-col items-end mt-auto gap-3 w-full md:w-[360px] ">
                <div class="flex md:flex-col max-md:items-end max-md:justify-between gap-1.5 max-md:w-full">
                    <div class="text-lg md:text-base font-semibold text-gray-900 order-2 md:order-1">
                        <NumberFormatComponent :number="cart.totalPrice(data.id)"
                                               append_text=" ₽" prepend_text="Итого: "/>
                    </div>
                    <CartQuantityStepper
                        class="order-1 md:order-2"
                        v-model="qtyProxy"
                        :min="cart.minQty(data.id)"
                        :step="cart.step(data.id)"
                        :allowDecimal="cart.uiOf(data.id).mode === 't'"
                        :disabled="!data.available"
                        size="md"
                        width-class="w-32"
                        @inc="cart.incQty(data.id)"
                        @dec="cart.decQty(data.id)"
                    />
                </div>
            </div>

            <!-- Избранное/Сравнение/Удалить -->
            <div class="absolute end-4 top-4 flex gap-2">
                <button
                    class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                    @click.prevent="compareStore.toggleCompare(data.id)"
                    :aria-pressed="compareStore.isCompared(data.id)"
                    :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнении'">
                    <IconScales v-if="compareStore.isCompared(data.id)" class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-blue-500"/>
                    <IconScales v-else class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-gray-700 group-hover:text-blue-500 transition"/>
                </button>
                <button
                    class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                    @click.prevent="likeStore.toggleLike(data.id)"
                    :aria-pressed="likeStore.isLiked(data.id)"
                    :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                    <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="h-5 w-5 text-likeColor"/>
                    <IconHeart v-else class="h-5 w-5 text-gray-700 group-hover:text-likeColor transition"/>
                </button>
                <button
                    class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition"
                    @click.prevent="cart.remove(data.id, cart.uiOf(data.id).mode)"
                    title="Удалить">
                    <IconTrash class="h-5 w-5 text-gray-700 md:group-hover:text-likeColor transition"/>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
